import React from 'react'
import styled from 'styled-components'
import { trackDownload } from '../lib/tracking'
import { useTranslation } from '../lib/i18n'

const Button = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 14px 28px;
    border-radius: 12px;
    text-decoration: none;
    font-weight: 600;
    font-size: 16px;
    transition: all 0.2s ease;
    cursor: pointer;
    width: 180px;

    svg {
        flex-shrink: 0;
    }

    .button-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        line-height: 1.2;
    }

    .small-text {
        font-size: 11px;
        font-weight: 400;
        opacity: 0.9;
    }

    .big-text {
        font-size: 18px;
        font-weight: 600;
    }
`

const MacButton = styled(Button)`
    background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
    color: white;
    border: 1px solid #444;

    &:hover {
        background: linear-gradient(135deg, #2a2a2a 0%, #444 100%);
        color: white;
    }

`

const WindowsButton = styled(Button)`
    background: linear-gradient(135deg, #0078d4 0%, #106ebe 100%);
    color: white;
    border: 1px solid #0078d4;

    &:hover {
        background: linear-gradient(135deg, #1084e0 0%, #1a7fd4 100%);
        color: white;
    }

    &.disabled {
        background: linear-gradient(135deg, #999 0%, #bbb 100%);
        border-color: #999;
        cursor: default;
        opacity: 0.7;
    }
`

const Dropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 8px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    opacity: ${({ show }) => (show ? 1 : 0)};
    visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
    transform: ${({ show }) => (show ? 'translateY(0)' : 'translateY(-10px)')};
    transition: all 0.2s ease;
    white-space: nowrap;
    z-index: 100;
`

const DropdownItem = styled.a`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    color: #333;
    text-decoration: none;
    font-size: 14px;
    border-bottom: 1px solid #eee;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #f5f5f5;
        color: #333;
    }

    svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }

    .chip {
        font-size: 11px;
        color: #666;
        margin-left: 4px;
    }

`

const AppleLogo = () => (
    <svg width="28" height="28" viewBox="0 0 512 512" fill="currentColor">
        <path d="M185.255,512c-76.201-0.439-139.233-155.991-139.233-235.21c0-129.404,97.075-157.734,134.487-157.734 c16.86,0,34.863,6.621,50.742,12.48c11.104,4.087,22.588,8.306,28.975,8.306c3.823,0,12.832-3.589,20.786-6.738 c16.963-6.753,38.071-15.146,62.651-15.146c0.044,0,0.103,0,0.146,0c18.354,0,74.004,4.028,107.461,54.272l7.837,11.777 l-11.279,8.511c-16.113,12.158-45.513,34.336-45.513,78.267c0,52.031,33.296,72.041,49.292,81.665 c7.061,4.248,14.37,8.628,14.37,18.208c0,6.255-49.922,140.566-122.417,140.566c-17.739,0-30.278-5.332-41.338-10.034 c-11.191-4.761-20.845-8.862-36.797-8.862c-8.086,0-18.311,3.823-29.136,7.881C221.496,505.73,204.752,512,185.753,512H185.255z"/>
        <path d="M351.343,0c1.888,68.076-46.797,115.304-95.425,112.342C247.905,58.015,304.54,0,351.343,0z"/>
    </svg>
)

const AppleLogoSmall = () => (
    <svg width="16" height="16" viewBox="0 0 512 512" fill="#333">
        <path d="M185.255,512c-76.201-0.439-139.233-155.991-139.233-235.21c0-129.404,97.075-157.734,134.487-157.734 c16.86,0,34.863,6.621,50.742,12.48c11.104,4.087,22.588,8.306,28.975,8.306c3.823,0,12.832-3.589,20.786-6.738 c16.963-6.753,38.071-15.146,62.651-15.146c0.044,0,0.103,0,0.146,0c18.354,0,74.004,4.028,107.461,54.272l7.837,11.777 l-11.279,8.511c-16.113,12.158-45.513,34.336-45.513,78.267c0,52.031,33.296,72.041,49.292,81.665 c7.061,4.248,14.37,8.628,14.37,18.208c0,6.255-49.922,140.566-122.417,140.566c-17.739,0-30.278-5.332-41.338-10.034 c-11.191-4.761-20.845-8.862-36.797-8.862c-8.086,0-18.311,3.823-29.136,7.881C221.496,505.73,204.752,512,185.753,512H185.255z"/>
        <path d="M351.343,0c1.888,68.076-46.797,115.304-95.425,112.342C247.905,58.015,304.54,0,351.343,0z"/>
    </svg>
)

const WindowsLogo = () => (
    <svg width="28" height="28" viewBox="0 0 32 32" fill="currentColor">
        <path d="M0 4.691l13.056-1.782v12.603H0V4.691zm14.533-2.004L31.997 0v15.516H14.533V2.687zM0 16.903h13.056v12.612L0 27.717V16.903zm14.533.013h17.464V32l-17.464-2.486V16.916z"/>
    </svg>
)

export const DownloadMacButton = ({ href, onClick, className }) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'store' })

    return (
        <MacButton href={href} onClick={(e) => { trackDownload('mac-arm'); onClick && onClick(e) }} className={className}>
            <AppleLogo />
            <span className="button-text">
                <span className="small-text">{t('downloadFor')}</span>
                <span className="big-text">{t('macOS')}</span>
            </span>
        </MacButton>
    )
}

export const DownloadWindowsButton = ({ href, disabled, className, onClick }) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'store' })

    return (
        <WindowsButton
            as={disabled ? 'span' : 'a'}
            href={disabled ? undefined : href}
            className={`${className || ''} ${disabled ? 'disabled' : ''}`}
            onClick={disabled ? undefined : (e) => { onClick && onClick(e) }}
            role={disabled ? 'presentation' : undefined}
        >
            <WindowsLogo />
            <span className="button-text">
                <span className="small-text">{disabled ? t('comingSoon') : t('downloadFor')}</span>
                <span className="big-text">{t('windows')}</span>
            </span>
        </WindowsButton>
    )
}

export const MacDropdown = ({ show, armUrl, intelUrl }) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'store' })

    return (
        <Dropdown show={show}>
            <DropdownItem href={armUrl} onClick={() => trackDownload('mac-arm')}>
                {t('appleSilicon')} <span className="chip">{t('mSeries')}</span>
            </DropdownItem>
            <DropdownItem href={intelUrl} onClick={() => trackDownload('mac-intel')}>
                {t('intelMac')}
            </DropdownItem>
        </Dropdown>
    )
}

export default { DownloadMacButton, DownloadWindowsButton, MacDropdown }
